// api/percentageTaxAPI.js
import axiosInstance from './axiosConfig';

const percentageTaxAPI = {
    // Get all percentage tax records for a client
    getByClient: async (clientId, params = {}) => {
        try {
            console.log('[TAX API] Calling percentage endpoint for client:', clientId, 'params:', params);
            const response = await axiosInstance.get(`/percentage-tax/client/${clientId}`, { params });
            return response.data;
        } catch (error) {
            console.error('Percentage Tax API Error:', error.response?.data || error.message);
            throw error.response?.data?.message || 'Failed to fetch Percentage Tax records';
        }
    },

    // Create percentage tax records
    createRecords: async (clientId, records, quarter, fiscalYear) => {
        try {
            const quarterNum = parseInt(quarter);
            const fiscalYearNum = parseInt(fiscalYear);
            
            console.log('Creating Percentage Tax records:', {
                clientId,
                recordsCount: records.length,
                quarter: quarterNum,
                fiscalYear: fiscalYearNum,
                records: records
            });

            const payload = {
                records,
                quarter: quarterNum,
                fiscalYear: fiscalYearNum
            };

            const response = await axiosInstance.post(`/percentage-tax/client/${clientId}`, payload);
            console.log('Percentage Tax records created:', response.data);
            return response.data;
        } catch (error) {
            console.error('Percentage Tax Creation Error:', {
                error: error.response?.data || error.message,
                status: error.response?.status
            });

            // Throws an Error, not a bare string like the other calls here, so
            // the caller can tell "the server rejected this" from "we never
            // heard back". Those need opposite handling: a rejected batch
            // definitely saved nothing and is safe to resubmit, while a dropped
            // response may have saved everything and must not be blindly
            // retried. PercentageTaxForm is the only caller.
            const failure = new Error(
                error.response?.data?.message || error.message || 'Failed to create Percentage Tax records'
            );
            failure.status = error.response?.status ?? null;
            failure.hasResponse = Boolean(error.response);
            failure.code = error.code || null;
            throw failure;
        }
    },

    // Debug endpoint
    debugClientRecords: async (clientId) => {
        try {
            const response = await axiosInstance.get(`/percentage-tax/debug/client/${clientId}`);
            return response.data;
        } catch (error) {
            console.error('Percentage Tax Debug Error:', error.response?.data || error.message);
            throw error.response?.data?.message || 'Failed to debug Percentage Tax records';
        }
    },


    updateRecord: async (recordId, payload) => {
        const res = await axiosInstance.put(
        `/percentage-tax/${recordId}`,
        payload
        );
        return res.data;
    },

    adjustGLRecord: async (clientId, payload) => {
        try {
            const response = await axiosInstance.post(`/percentage-tax/clients/${clientId}/adjust-gl-record`, payload);
            return response.data;
        } catch (error) {
            throw error.response?.data?.message || 'Failed to adjust Percentage Tax GL record';
        }
    },

    getGLAuditTrail: async (clientId, recordId) => {
        try {
            const response = await axiosInstance.get(`/percentage-tax/clients/${clientId}/records/${recordId}/gl-audit`);
            return response.data;
        } catch (error) {
            throw error.response?.data?.message || 'Failed to fetch Percentage Tax GL audit trail';
        }
    },

    deleteRecord: async (recordId) => {
        const res = await axiosInstance.delete(
        `/percentage-tax/${recordId}`
        );
        return res.data;
    },


    // Get percentage tax summary
    getSummary: async (clientId, params = {}) => {
        try {
            const response = await axiosInstance.get(`/percentage-tax/summary/client/${clientId}`, { params });
            return response.data;
        } catch (error) {
            console.error('Percentage Tax Summary Error:', error.response?.data || error.message);
            throw error.response?.data?.message || 'Failed to fetch Percentage Tax summary';
        }
    },

    // Quarter summary and adjustment (mirror VAT API)
    getQuarterSummary: async (clientId, params = {}) => {
        try {
            const response = await axiosInstance.get(`/percentage-tax/quarter-summary/client/${clientId}`, { params });
            return response.data;
        } catch (error) {
            console.error('Percentage Tax Quarter Summary Error:', error.response?.data || error.message);
            throw error.response?.data?.message || 'Failed to fetch Percentage Tax quarter summary';
        }
    },

    saveQuarterAdjustment: async (clientId, payload) => {
        try {
            const response = await axiosInstance.post(`/percentage-tax/clients/${clientId}/quarter-adjustment`, payload);
            return response.data;
        } catch (error) {
            // Backward compatibility: older backend may still expose PUT /quarter-adjustment/client/:clientId
            try {
                const fallback = await axiosInstance.put(`/percentage-tax/quarter-adjustment/client/${clientId}`, payload);
                return fallback.data;
            } catch (fallbackError) {
                console.error('Percentage Tax Quarter Adjustment Error:', fallbackError.response?.data || fallbackError.message);
                throw fallbackError.response?.data?.message || error.response?.data?.message || 'Failed to save Percentage Tax quarter adjustment';
            }
        }
    },

    getQuarterAdjustment: async (clientId, params = {}) => {
        try {
            const response = await axiosInstance.get(`/percentage-tax/clients/${clientId}/quarter-adjustment`, { params });
            return response.data;
        } catch (error) {
            // Backward compatibility: if dedicated endpoint isn't available yet,
            // derive excess from quarter summary and return a compatible shape.
            try {
                const summary = await axiosInstance.get(`/percentage-tax/quarter-summary/client/${clientId}`, { params });
                const excess = Number(summary?.data?.data?.excessFromPrevQuarter || 0);
                return {
                    success: true,
                    data: {
                        excessFromPrevQuarter: excess,
                        notes: '',
                    },
                };
            } catch (fallbackError) {
                console.error('Percentage Tax Quarter Adjustment Fetch Error:', fallbackError.response?.data || fallbackError.message);
                throw fallbackError.response?.data?.message || error.response?.data?.message || 'Failed to fetch Percentage Tax quarter adjustment';
            }
        }
    },

    // Helper functions for Percentage Tax
    calculateTax: (grossSales, taxRate) => {
        const gross = parseFloat(grossSales) || 0;
        const rate = parseFloat(taxRate) || 0;
        const taxDue = (gross * rate) / 100;
        
        return {
            grossSales: gross,
            taxDue: parseFloat(taxDue.toFixed(2)),
            taxRate: rate
        };
    },

    // Get BIR percentage tax rates for Form 2551Q
    getTaxRates: () => {
        return [
            { value: '1.00', label: '1% - Banks, non-banks, finance companies' },
            { value: '2.00', label: '2% - Insurance companies' },
            { value: '3.00', label: '3% - Standard rate (most common)' },
            { value: '5.00', label: '5% - Amusement places' },
            { value: '7.00', label: '7% - Race tracks' },
            { value: '10.00', label: '10% - Jai-alai and cockpits' },
            { value: '15.00', label: '15% - Cabarets, night clubs' },
            { value: '18.00', label: '18% - Boxing exhibitions' },
            { value: '30.00', label: '30% - Overseas dispatch' }
        ];
    },

    // Common ATC (Alphabetical Tax Code) for Percentage Tax
    getATCCodes: () => {
        return [
            { value: 'PT010', label: 'PT010 - Sale of Goods/Properties' },
            { value: 'PT020', label: 'PT020 - Sale of Services' },
            { value: 'PT030', label: 'PT030 - Leasing of Properties' },
            { value: 'PT040', label: 'PT040 - Lending of Money' },
            { value: 'PT050', label: 'PT050 - Transportation' },
            { value: 'PT060', label: 'PT060 - Amusement' },
            { value: 'PT070', label: 'PT070 - Professional Services' },
            { value: 'PT080', label: 'PT080 - Other Services' }
        ];
    },

    // Get status options
    getStatusOptions: () => {
        return [
            { value: 'Pending', label: 'Pending', color: 'yellow' },
            { value: 'Paid', label: 'Paid', color: 'green' },
            { value: 'Partially Paid', label: 'Partially Paid', color: 'blue' },
            { value: 'Overdue', label: 'Overdue', color: 'red' },
            { value: 'Exempt', label: 'Exempt', color: 'purple' },
            { value: 'Cancelled', label: 'Cancelled', color: 'gray' }
        ];
    },

    // Calculate due date based on quarter
    calculateDueDate: (quarter, fiscalYear) => {
        if (!quarter || !fiscalYear) return null;
        
        const dueDates = {
            1: new Date(fiscalYear, 3, 25), // April 25 for Q1
            2: new Date(fiscalYear, 6, 25), // July 25 for Q2
            3: new Date(fiscalYear, 9, 25), // October 25 for Q3
            4: new Date(fiscalYear + 1, 0, 25) // January 25 for Q4
        };
        
        return dueDates[quarter]?.toISOString().split('T')[0] || null;
    },

    // Validate if amount is exempt based on threshold
    isExempt: (grossSales, threshold = 3000000) => {
        return parseFloat(grossSales || 0) < threshold;
    },

    // helpers for quarter and fiscal-year selectors
    getFiscalYearOptions: (yearsRange = 2) => {
        const currentYear = new Date().getFullYear();
        const options = [];
        for (let i = -yearsRange; i <= yearsRange; i++) {
            const year = currentYear + i;
            options.push({ value: year, label: year.toString(), isCurrent: i === 0 });
        }
        return options;
    },

    getQuarterOptions: () => [
        { value: 1, label: 'Q1 (Jan - Mar)' },
        { value: 2, label: 'Q2 (Apr - Jun)' },
        { value: 3, label: 'Q3 (Jul - Sep)' },
        { value: 4, label: 'Q4 (Oct - Dec)' },
    ],

    // Format currency with PHP symbol
    formatCurrency: (amount) => {
        if (amount === null || amount === undefined || isNaN(amount)) return '₱0.00';
        return `₱${amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
    }
};

export default percentageTaxAPI;